import React from 'react';
import { Flame } from 'lucide-react';
import { fmtPrice } from '@/lib/format';
import { TID } from '@/lib/testIds';
import { SafeImage } from '@/components/SafeImage';

/**
 * MenuItemCard — Phase 10 browsing-only view.
 *
 * No ordering interactions. Variant pricing (sizes) is shown inline as informational
 * text. Genuine SmithyBrowns imagery is used via SafeImage which gracefully falls back
 * to the branded silhouette when an image is unset/broken.
 *
 * Badges (top-left of image):
 *   Halal      → small green circular mark with the Arabic word "حلال" (universally
 *                recognised halal-certification symbol). Driven by `tags.includes('halal')`.
 *   Signature  → small orange flame icon. Driven by the existing `featured` /
 *                `show_as_featured_dish` flag.
 */
export const MenuItemCard = ({ item }) => {
  const hasSizes = item.has_sizes && Array.isArray(item.sizes) && item.sizes.length > 0;
  const singlePrice = !hasSizes ? item.price : null;
  const isHalal = (item.tags || []).includes('halal');
  const isVeg = (item.tags || []).includes('veg');
  const isSignature = !!(item.show_as_featured_dish ?? item.featured);

  return (
    <article data-testid={TID.menu.card} className="sb-card overflow-hidden flex flex-col group">
      {/* Compact 16:9 image ~25% shorter than the previous 4:3 for higher information density */}
      <div className="aspect-video relative bg-black">
        <SafeImage
          src={item.image_url}
          alt={item.name}
          className="absolute inset-0 h-full w-full"
          imgClassName="absolute inset-0 h-full w-full object-cover group-hover:scale-[1.03] transition-transform duration-300"
          fallbackIconSize={28}
        />
        {!item.available && (
          <div className="absolute inset-0 bg-black/70 grid place-items-center">
            <span className="text-white text-xs uppercase tracking-widest border border-white/30 px-3 py-1 rounded-full">
              Sold out
            </span>
          </div>
        )}
        <div className="absolute top-2 left-2 flex items-center gap-1.5">
          {isHalal && (
            <span
              title="Halal certified"
              aria-label="Halal certified"
              data-testid="menu-item-halal-badge"
              className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-[hsl(var(--sb-green))] text-black text-[9px] font-black leading-none shadow-md shadow-black/40 ring-1 ring-black/20"
              style={{ fontFamily: '"Noto Sans Arabic", "Segoe UI", Tahoma, sans-serif' }}
            >
              حلال
            </span>
          )}
          {isSignature && (
            <span
              title="Signature dish"
              aria-label="Signature dish"
              data-testid="menu-item-signature-badge"
              className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-black/45 backdrop-blur-sm ring-1 ring-orange-500/40 shadow-md shadow-black/40"
            >
              <Flame size={14} className="text-orange-400" fill="currentColor" />
            </span>
          )}
          {isVeg && (
            <span className="text-[10px] uppercase font-semibold tracking-wide px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">
              Veg
            </span>
          )}
        </div>
      </div>

      <div className="p-3.5 sm:p-4 flex-1 flex flex-col">
        <div className="flex items-start justify-between gap-2">
          <h3 className="font-display text-base sm:text-lg leading-tight text-white uppercase">
            {item.name}
          </h3>
          {singlePrice != null && (
            <span className="font-display text-lg text-[hsl(var(--sb-gold))] whitespace-nowrap">
              {fmtPrice(singlePrice)}
            </span>
          )}
        </div>

        {item.description ? (
          <p className="text-xs text-white/65 mt-1 line-clamp-2">{item.description}</p>
        ) : null}

        {/* Variant pricing — read-only, non-interactive */}
        {hasSizes && (
          <div className="mt-2 flex flex-wrap items-baseline gap-x-3 gap-y-1">
            {item.sizes.map((s, idx) => (
              <span key={s.name} className="text-xs text-white/85">
                <span className="text-white/60 uppercase tracking-widest text-[10px] mr-1">
                  {s.name}
                </span>
                <span className="font-display text-[hsl(var(--sb-gold))]">{fmtPrice(s.price)}</span>
                {idx < item.sizes.length - 1 && (
                  <span className="text-white/25 ml-2" aria-hidden="true">·</span>
                )}
              </span>
            ))}
          </div>
        )}
      </div>
    </article>
  );
};
